import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff, Phone, MapPin, Gauge, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Register = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', password: '', confirmPassword: '',
    phone: '', address: '', pdl_linky: '', user_type: 'consumer',
    subscribed_power_kva: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(false);
  const [rgpdConsent, setRgpdConsent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('L\'adresse est obligatoire');
      return;
    }
    if (!formData.pdl_linky.trim()) {
      toast.error('Le numéro PDL Linky est obligatoire');
      return;
    }
    if (!rgpdConsent) {
      toast.error('Vous devez accepter la politique de confidentialité');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
          address: formData.address,
          pdl_linky: formData.pdl_linky,
          user_type: formData.user_type,
          subscribed_power_kva: formData.subscribed_power_kva ? parseFloat(formData.subscribed_power_kva) : null,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setRegistrationDone(true);
      } else {
        toast.error(data.detail || "Erreur lors de l'inscription");
      }
    } catch {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Success screen after registration
  if (registrationDone) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#f8fafc]" data-testid="registration-pending-screen">
        <div className="w-full max-w-lg text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 border-2 border-green-200 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 tracking-tight">
            Inscription enregistrée
          </h1>
          <Card className="border border-green-100/60 shadow-sm mt-6">
            <CardContent className="p-6 sm:p-8">
              <p className="text-muted-foreground leading-relaxed mb-4">
                Votre demande d'inscription a bien été enregistrée.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
                <p className="text-amber-800 text-sm font-medium mb-2">En attente de validation</p>
                <p className="text-amber-700 text-sm leading-relaxed">
                  Un administrateur va vérifier vos informations et valider votre inscription.
                  Vous recevrez un email de confirmation à <strong>{formData.email}</strong> dès que votre compte sera activé.
                </p>
              </div>
              <div className="mt-6 p-4 bg-slate-50 rounded-xl text-left space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Récapitulatif</p>
                <p className="text-sm"><span className="text-muted-foreground">Nom :</span> <strong>{formData.name}</strong></p>
                <p className="text-sm"><span className="text-muted-foreground">Email :</span> {formData.email}</p>
                <p className="text-sm"><span className="text-muted-foreground">Adresse :</span> {formData.address}</p>
                <p className="text-sm"><span className="text-muted-foreground">PDL Linky :</span> {formData.pdl_linky}</p>
                <p className="text-sm"><span className="text-muted-foreground">Puissance :</span> {formData.subscribed_power_kva ? `${formData.subscribed_power_kva} kVA` : 'Non renseignée'}</p>
                <p className="text-sm"><span className="text-muted-foreground">Type :</span> {formData.user_type === 'producer' ? 'Producteur' : 'Consommateur'}</p>
              </div>
            </CardContent>
          </Card>
          <Link to="/login" className="inline-block mt-6 text-green-600 hover:text-green-700 font-semibold text-sm" data-testid="back-to-login-link">
            Retour à la page de connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-[45%] relative bg-gradient-to-br from-green-600 to-green-800 items-center justify-center p-12">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="Green Teva" className="w-12 h-12 rounded-xl" />
            <span className="text-2xl font-bold">Green Teva</span>
          </div>
          <h2 className="text-3xl font-bold mb-4 leading-tight">
            Rejoignez la communauté Green Teva
          </h2>
          <p className="text-green-100 text-base leading-relaxed">
            Créez votre compte pour commencer à partager et consommer de l'énergie renouvelable locale avec vos voisins.
          </p>
          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-green-100">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-white">1</span>
              </div>
              <span className="text-sm">Remplissez le formulaire d'inscription</span>
            </div>
            <div className="flex items-center gap-3 text-green-100">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-white">2</span>
              </div>
              <span className="text-sm">Un administrateur valide vos informations</span>
            </div>
            <div className="flex items-center gap-3 text-green-100">
              <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <span className="text-sm font-bold text-white">3</span>
              </div>
              <span className="text-sm">Commencez à échanger de l'énergie</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-[#f8fafc] overflow-y-auto">
        <div className="w-full max-w-md py-6">
          <div className="flex lg:hidden items-center gap-2 mb-6">
            <img src="/logo.png" alt="Green Teva" className="w-9 h-9 rounded-xl" />
            <span className="text-lg font-bold text-green-700">Green Teva</span>
          </div>

          <div className="mb-5">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              Créer un compte
            </h1>
            <p className="text-muted-foreground text-sm mt-2">
              Inscrivez-vous pour rejoindre une communauté énergétique
            </p>
          </div>

          <Card className="border border-green-100/60 shadow-sm">
            <CardContent className="p-5 sm:p-7">
              <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="register-form">
                {/* Name + Email */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="name" className="text-xs font-medium">Nom complet *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="name" name="name" placeholder="Jean Dupont"
                        value={formData.name} onChange={handleChange}
                        className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                        required data-testid="register-name-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-xs font-medium">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="email" name="email" type="email" placeholder="vous@exemple.com"
                        value={formData.email} onChange={handleChange}
                        className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                        required data-testid="register-email-input" />
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-1.5">
                  <Label htmlFor="phone" className="text-xs font-medium">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="phone" name="phone" placeholder="06 12 34 56 78"
                      value={formData.phone} onChange={handleChange}
                      className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                      data-testid="register-phone-input" />
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-1.5">
                  <Label htmlFor="address" className="text-xs font-medium">Adresse complète *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="address" name="address" placeholder="12 rue des Lilas, 75011 Paris"
                      value={formData.address} onChange={handleChange}
                      className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                      required data-testid="register-address-input" />
                  </div>
                </div>

                {/* PDL Linky */}
                <div className="space-y-1.5">
                  <Label htmlFor="pdl_linky" className="text-xs font-medium">Point de Livraison (PDL) Linky *</Label>
                  <div className="relative">
                    <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="pdl_linky" name="pdl_linky" placeholder="Ex: 09 123 456 789 012"
                      value={formData.pdl_linky} onChange={handleChange}
                      className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                      required data-testid="register-pdl-input" />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Numéro à 14 chiffres, visible sur votre facture ou votre compteur Linky</p>
                </div>

                {/* Puissance souscrite */}
                <div className="space-y-1.5">
                  <Label htmlFor="subscribed_power_kva" className="text-xs font-medium">Puissance souscrite (kVA)</Label>
                  <div className="relative">
                    <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input id="subscribed_power_kva" name="subscribed_power_kva" type="number" placeholder="Ex: 6, 9, 12, 36..."
                      value={formData.subscribed_power_kva} onChange={handleChange}
                      className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                      data-testid="register-power-input" />
                  </div>
                  <p className="text-[11px] text-muted-foreground">Visible sur votre facture. Particuliers : généralement 6 à 36 kVA</p>
                </div>

                {/* User type */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Type de compte *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button"
                      onClick={() => setFormData({ ...formData, user_type: 'consumer' })}
                      className={`p-3 rounded-xl border-2 text-center transition-all text-sm font-medium ${
                        formData.user_type === 'consumer'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-slate-200 text-muted-foreground hover:border-green-200'
                      }`}
                      data-testid="register-consumer-btn">
                      Consommateur
                    </button>
                    <button type="button"
                      onClick={() => setFormData({ ...formData, user_type: 'producer' })}
                      className={`p-3 rounded-xl border-2 text-center transition-all text-sm font-medium ${
                        formData.user_type === 'producer'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-slate-200 text-muted-foreground hover:border-green-200'
                      }`}
                      data-testid="register-producer-btn">
                      Producteur
                    </button>
                  </div>
                </div>

                {/* Passwords */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-xs font-medium">Mot de passe *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="password" name="password" type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 6 car."
                        value={formData.password} onChange={handleChange}
                        className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                        required data-testid="register-password-input" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword" className="text-xs font-medium">Confirmer *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input id="confirmPassword" name="confirmPassword" type={showPassword ? 'text' : 'password'}
                        placeholder="Confirmer"
                        value={formData.confirmPassword} onChange={handleChange}
                        className="pl-10 h-10 bg-white border-green-200/80 rounded-xl text-sm"
                        required data-testid="register-confirm-password-input" />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" onClick={() => setShowPassword(!showPassword)}
                    className="rounded border-green-300 text-green-600 focus:ring-green-500" />
                  <span className="text-xs text-muted-foreground">Afficher les mots de passe</span>
                </div>

                <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <input
                    type="checkbox"
                    checked={rgpdConsent}
                    onChange={(e) => setRgpdConsent(e.target.checked)}
                    className="rounded border-green-300 text-green-600 focus:ring-green-500 mt-0.5"
                    data-testid="rgpd-consent-checkbox"
                    required
                  />
                  <span className="text-xs text-muted-foreground leading-relaxed">
                    J'accepte que mes donnees personnelles soient traitees conformement a la{' '}
                    <Link to="/confidentialite" target="_blank" className="text-green-600 underline">
                      Politique de confidentialite
                    </Link>,{' '}
                    aux{' '}
                    <Link to="/cgu" target="_blank" className="text-green-600 underline">
                      CGU
                    </Link>{' '}
                    et aux{' '}
                    <Link to="/cgv" target="_blank" className="text-green-600 underline">
                      CGV
                    </Link>. *
                  </span>
                </div>

                <Button type="submit" className="w-full btn-primary h-11 text-base" disabled={loading}
                  data-testid="register-submit-btn">
                  {loading ? (
                    <div className="loader !w-5 !h-5 !border-white/30 !border-t-white" />
                  ) : (
                    <>Envoyer ma demande d'inscription<ArrowRight className="ml-2 w-4 h-4" /></>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-5">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-green-600 hover:text-green-700 font-semibold" data-testid="login-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
